/**
 * Export utilities for PDF and CSV exports
 * Provides functions to format and prepare data for export
 */

import { AppColors } from './theme';
import type { BusinessReviewStats } from '@/services/review.service';

// Common interface for all exportable data
export interface ExportableData {
  title: string;
  subtitle?: string;
  date: string;
  data: any;
}

// Analytics data interfaces
export interface BusinessAnalytics {
  totalRevenue: number;
  totalOrders: number;
  activeProducts: number;
  activeCustomers: number;
  foodInventory: any;
  topSellingProducts: Record<string, number>;
  productCategories: Record<string, number>;
  customerDemographics: any;
  salesData?: any[];
  orderStatus?: any[];
  customerData?: any;
  productData?: any;
}

export interface CharityAnalytics {
  volunteerStats: {
    total: number;
    accepted: number;
    rejected: number;
    thisMonth: number;
    lastMonth: number;
    thisYear: number;
  };
  collectionStats: {
    total: number;
    thisMonth: number;
    lastMonth: number;
    thisYear: number;
    monthOverMonthChange: number;
  };
  eventStats: {
    total: number;
    thisMonth: number;
    lastMonth: number;
    thisYear: number;
    totalAttendees: number;
    thisMonthAttendees: number;
    lastMonthAttendees: number;
    monthOverMonthChange: number;
  };
  timeDistribution: {
    [key: string]: number;
  };
}

// Format date for export file names
export const formatDateForFileName = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

// Format business review stats for CSV export
export const formatBusinessReviewStatsForCSV = (stats: BusinessReviewStats): any[] => {
  if (!stats) return [];

  // Basic info
  const basicInfo = [
    { Category: 'Average Rating', Value: stats.average_rating || 0 },
    { Category: 'Total Reviews', Value: stats.total_reviews || 0 },
  ];

  // Rating distribution
  const ratingDistribution = Object.entries(stats.rating_distribution || {}).map(([rating, count]) => ({
    Category: `${rating} Star Reviews`,
    Value: count,
  }));

  // Category percentages
  const categoryPercentages = [
    { Category: 'Timely Delivery', Value: `${(stats.timely_delivery_percent * 100).toFixed(1)}%` },
    { Category: 'Sufficient Quantity', Value: `${(stats.sufficient_quantity_percent * 100).toFixed(1)}%` },
    { Category: 'Easy Collection', Value: `${(stats.easy_collection_percent * 100).toFixed(1)}%` },
  ];

  // Recent comments (if available)
  const recentComments = (stats.recent_comments || []).map((comment: any, index: number) => ({
    Category: `Comment ${index + 1}`,
    Value: comment.comment,
    Rating: comment.rating,
    Date: new Date(comment.created_at).toLocaleDateString(),
  }));

  return [...basicInfo, ...ratingDistribution, ...categoryPercentages, ...recentComments];
};

// Format charity review stats for CSV export
export const formatCharityReviewStatsForCSV = (stats: any): any[] => {
  if (!stats) return [];

  // Basic info
  const basicInfo = [
    { Category: 'Charity Name', Value: stats.charity_name || 'N/A' },
    { Category: 'Average Rating', Value: stats.average_rating || 0 },
    { Category: 'Total Reviews', Value: stats.total_reviews || 0 },
  ];

  // Rating distribution
  const ratingDistribution = Object.entries(stats.rating_distribution || {}).map(([rating, count]) => ({
    Category: `${rating} Star Reviews`,
    Value: count,
  }));

  // Category ratings
  const categoryRatings = Object.entries(stats.category_ratings || {}).map(([category, rating]) => ({
    Category: `${category} Rating`,
    Value: `${(Number(rating) * 100).toFixed(1)}%`,
  }));

  // Recent comments (if available)
  const recentComments = (stats.recent_comments || []).map((comment: any, index: number) => ({
    Category: `Comment ${index + 1}`,
    Value: comment.comment,
    Rating: comment.rating,
    Date: new Date(comment.created_at).toLocaleDateString(),
  }));

  return [...basicInfo, ...ratingDistribution, ...categoryRatings, ...recentComments];
};

// Format social impact data for CSV export
export const formatSocialImpactForCSV = (data: any): any[] => {
  if (!data) return [];

  // Basic metrics
  const basicMetrics = [
    { Metric: 'Total Donations', Value: data.total_donations || 0 },
    { Metric: 'Total Value', Value: `$${data.total_value?.toFixed(2) || '0.00'}` },
    { Metric: 'Charities Supported', Value: data.charities_supported || 0 },
    { Metric: 'People Impacted', Value: data.people_impacted || 0 },
  ];

  // Monthly donations
  const monthlyDonations = (data.monthly_donations || []).map((item: any) => ({
    Month: item.month,
    Donations: item.donations,
    Value: `$${item.value?.toFixed(2) || '0.00'}`,
  }));

  // Donation categories
  const donationCategories = (data.donation_categories || []).map((item: any) => ({
    Category: item.category,
    Percentage: `${(item.percentage * 100).toFixed(1)}%`,
    Count: item.count,
  }));

  return [...basicMetrics, { Metric: 'Monthly Donations', Value: '' }, ...monthlyDonations, 
          { Metric: 'Donation Categories', Value: '' }, ...donationCategories];
};

// Format analytics data for CSV export
export const formatAnalyticsForCSV = (data: any): any[] => {
  if (!data) return [];

  // Basic metrics
  const basicMetrics = [
    { Metric: 'Total Revenue', Value: `$${data.total_revenue?.toFixed(2) || '0.00'}` },
    { Metric: 'Total Orders', Value: data.total_orders || 0 },
    { Metric: 'Average Order Value', Value: `$${data.average_order_value?.toFixed(2) || '0.00'}` },
  ];

  // Monthly revenue
  const monthlyRevenue = (data.monthly_revenue || []).map((item: any) => ({
    Month: item.month,
    Revenue: `$${item.revenue?.toFixed(2) || '0.00'}`,
    Orders: item.orders,
  }));

  // Top products
  const topProducts = (data.top_products || []).map((item: any) => ({
    Product: item.name,
    Revenue: `$${item.revenue?.toFixed(2) || '0.00'}`,
    Quantity: item.quantity,
  }));

  return [...basicMetrics, { Metric: 'Monthly Revenue', Value: '' }, ...monthlyRevenue, 
          { Metric: 'Top Products', Value: '' }, ...topProducts];
};

// Format business analytics dashboard data for CSV export
export const formatBusinessAnalyticsForCSV = (stats: BusinessAnalytics): any[] => {
  if (!stats) return [];

  // Basic info
  const basicInfo = [
    { Category: 'Total Revenue', Value: `$${stats.totalRevenue.toFixed(2)}` },
    { Category: 'Total Orders', Value: stats.totalOrders },
    { Category: 'Active Products', Value: stats.activeProducts },
    { Category: 'Active Customers', Value: stats.activeCustomers },
  ];

  // Food inventory stats
  const inventoryStats = [
    { Category: 'Total Inventory', Value: stats.foodInventory?.total || 0 },
    { Category: 'Sold Items', Value: stats.foodInventory?.sold || 0 },
    { Category: 'Unsold Items', Value: stats.foodInventory?.unsold || 0 },
    { Category: 'Donated Items', Value: stats.foodInventory?.donated || 0 },
  ];

  // Top selling products
  const topProducts = Object.entries(stats.topSellingProducts || {}).map(([product, sales]) => ({
    Category: `Top Product: ${product}`,
    Value: sales,
  }));

  // Product categories
  const categories = Object.entries(stats.productCategories || {}).map(([category, percentage]) => ({
    Category: `Category: ${category}`,
    Value: `${percentage}%`,
  }));

  return [...basicInfo, ...inventoryStats, ...topProducts, ...categories];
};

// Format charity analytics dashboard data for CSV export
export const formatCharityAnalyticsForCSV = (stats: CharityAnalytics): any[] => {
  if (!stats) return [];

  // Volunteer stats
  const volunteerStats = [
    { Category: 'Total Volunteers', Value: stats.volunteerStats.total },
    { Category: 'Accepted Volunteers', Value: stats.volunteerStats.accepted },
    { Category: 'Rejected Volunteers', Value: stats.volunteerStats.rejected },
    { Category: 'Volunteers This Month', Value: stats.volunteerStats.thisMonth },
    { Category: 'Volunteers Last Month', Value: stats.volunteerStats.lastMonth },
    { Category: 'Volunteers This Year', Value: stats.volunteerStats.thisYear },
  ];

  // Collection stats
  const collectionStats = [
    { Category: 'Total Collections', Value: stats.collectionStats.total },
    { Category: 'Collections This Month', Value: stats.collectionStats.thisMonth },
    { Category: 'Collections Last Month', Value: stats.collectionStats.lastMonth },
    { Category: 'Collections This Year', Value: stats.collectionStats.thisYear },
    { Category: 'Month-over-Month Change', Value: `${stats.collectionStats.monthOverMonthChange.toFixed(1)}%` },
  ];

  // Event stats
  const eventStats = [
    { Category: 'Total Events', Value: stats.eventStats.total },
    { Category: 'Events This Month', Value: stats.eventStats.thisMonth },
    { Category: 'Events Last Month', Value: stats.eventStats.lastMonth },
    { Category: 'Events This Year', Value: stats.eventStats.thisYear },
    { Category: 'Total Attendees', Value: stats.eventStats.totalAttendees },
    { Category: 'Attendees This Month', Value: stats.eventStats.thisMonthAttendees },
    { Category: 'Attendees Last Month', Value: stats.eventStats.lastMonthAttendees },
    { Category: 'Month-over-Month Change', Value: `${stats.eventStats.monthOverMonthChange.toFixed(1)}%` },
  ];

  // Time distribution
  const timeDistribution = Object.entries(stats.timeDistribution || {}).map(([period, count]) => ({
    Category: `Period: ${period}`,
    Value: count,
  }));

  return [...volunteerStats, ...collectionStats, ...eventStats, ...timeDistribution];
};

// PDF styling constants
export const pdfStyles = {
  colors: {
    primary: AppColors.skyBlue,
    secondary: AppColors.navyBlue,
    accent: AppColors.lightGreen,
    text: AppColors.black,
    textSecondary: AppColors.darkGrey,
    background: AppColors.white,
    surface: AppColors.lightGrey,
  },
  fonts: {
    regular: 'Helvetica',
    bold: 'Helvetica-Bold',
  },
  spacing: {
    page: {
      padding: 40,
      margin: 10,
    },
    section: {
      marginBottom: 15,
    },
  },
  fontSize: {
    title: 18,
    subtitle: 14,
    heading: 12,
    normal: 10,
    small: 8,
  },
  borderRadius: 4,
};
